import React, { useEffect, useState } from 'react';
import MainLayout from '../layouts/MainLayout';
import { 
    Box, 
    Button, 
    CircularProgress, 
    Typography,
    Card,
    Alert,
    Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import TemplateMailingTable from "../components/TemplateMailingTable";
import {getTemplateMailing } from "../auth";
import { 
    Email,
    NotificationsActive,
    Warning,
    AccessTime,
    MarkEmailRead
} from '@mui/icons-material';

interface TemplateMailing {
    id: number;
    type: string;
    libelle: string;
}

const TemplateMailingManagement: React.FC = () => {
    const [templateMailing, setTemplateMailing] = useState<TemplateMailing[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const fetchTemplateMailing = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getTemplateMailing();
            setTemplateMailing(response as TemplateMailing[]);
        } catch (error) {
            setError("Erreur lors de la récupération des modèles de mail");
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplateMailing();
    }, []);

    const handleCreateTemplateMailing = () => {
        navigate('/parametrages/template-mailing/create');
    };

    const handleEditTemplateMailing = (template: TemplateMailing) => {
        navigate(`/parametrages/template-mailing/edit/${template.id}`);
    };

    const getTemplateStats = () => {
        const total = templateMailing.length;
        const welcome = templateMailing.filter(t => t.type === 'WELCOME').length;
        const notification = templateMailing.filter(t => t.type === 'NOTIFICATION').length;
        const alert = templateMailing.filter(t => t.type === 'ALERT').length;
        const reminder = templateMailing.filter(t => t.type === 'REMINDER').length;

        return { total, welcome, notification, alert, reminder };
    };

    const stats = getTemplateStats();

    return (
        <MainLayout>
            <Box className="p-6 space-y-6">
                {/* En-tête */}
                <Card className="bg-white/80 backdrop-blur-sm p-6">
                    <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <Typography variant="h4" className="text-emerald-800 font-bold mb-2">
                                Gestion des modèles de mail
                            </Typography>
                            <Typography variant="body1" className="text-emerald-600">
                                Gérez vos modèles de mail personnalisés
                            </Typography>
                        </div>
                        <Button
                            variant="contained"
                            onClick={handleCreateTemplateMailing}
                            startIcon={<AddIcon />}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap"
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                px: 3,
                                py: 1
                            }}
                        >
                            Créer un modèle
                        </Button>
                    </Box>
                </Card>

                {/* Messages d'erreur */}
                {error && (
                    <Alert 
                        severity="error" 
                        className="rounded-lg border border-red-100"
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {/* Statistiques */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-gray-600 text-sm">Total</Typography>
                                    <Typography variant="h4" className="font-bold text-gray-800">
                                        {stats.total}
                                    </Typography>
                                </div>
                                <Email className="text-gray-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-emerald-600 text-sm">Bienvenue</Typography>
                                    <Typography variant="h4" className="font-bold text-emerald-800">
                                        {stats.welcome}
                                    </Typography>
                                </div>
                                <MarkEmailRead className="text-emerald-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-blue-600 text-sm">Notifications</Typography>
                                    <Typography variant="h4" className="font-bold text-blue-800">
                                        {stats.notification}
                                    </Typography>
                                </div>
                                <NotificationsActive className="text-blue-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-red-600 text-sm">Alertes</Typography>
                                    <Typography variant="h4" className="font-bold text-red-800">
                                        {stats.alert}
                                    </Typography>
                                </div>
                                <Warning className="text-red-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-amber-600 text-sm">Rappels</Typography>
                                    <Typography variant="h4" className="font-bold text-amber-800">
                                        {stats.reminder}
                                    </Typography>
                                </div>
                                <AccessTime className="text-amber-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                </Grid>

                {/* Table des modèles */}
                <Card className="bg-white/80 backdrop-blur-sm overflow-hidden">
                    {loading ? (
                        <Box className="flex justify-center items-center p-12">
                            <CircularProgress className="text-emerald-600" />
                        </Box>
                    ) : (
                        <Box className="overflow-x-auto">
                            <TemplateMailingTable
                                templateMailing={templateMailing}
                                onEditTemplateMailing={handleEditTemplateMailing}
                                refreshTemplateMailing={fetchTemplateMailing}
                            />
                        </Box>
                    )}
                </Card>
            </Box>
        </MainLayout>
    );
};

export default TemplateMailingManagement;